import { createSelector } from "@reduxjs/toolkit";
import {
  CHARACTER_FIGHTER_ACTIONS,
  OPPONENT_FIGHTER_ACTIONS,
} from "redux/actions";
import {
  DamageModel,
  DerivedStatSets,
  Fighter,
  RootState,
  CharacterBaseStats,
  MobModel,
  FightStatuses,
  SkillModel,
  CombatLog,
  MobDialogsModel,
  MobImages,
  MobDrops,
  FightResults,
  FightAction,
} from "types";
import { getDerivedStats } from "./characterSelectors";
import {
  calculateCurrentBaseStats,
  calculateMobBaseStats,
  getDerivedStatSets,
} from "libs/stats";
import {
  getCurrentBaseStats,
  getPilotSkills,
  getTotalBaseStats,
} from "./characterSelectors";
import { getUserInfo } from "./characterSelectors";
import { buildFighterData, getFightTurns, getMobData } from "libs/fight";
import { getOpponentSkills } from "libs/skill";

// CHARACTER

export const getSkillsFightInfo = createSelector(
  (state: RootState) => state.character.data.skillsRecharge,
  (state: RootState) => state.character.data.skillsValues,
  (
    skillsRecharge,
    skillsValues
  ): {
    skillsRecharge: { [key: string]: number };
    skillsValues: { [key: string]: number };
  } => {
    return {
      skillsRecharge,
      skillsValues,
    };
  }
);

export const getCharacterDamage = (state: RootState): DamageModel[] =>
  state.character.data.damage;

export const getCharacterFighterData = createSelector(
  getUserInfo,
  getDerivedStats,
  getCurrentBaseStats,
  getTotalBaseStats,
  getPilotSkills,
  getSkillsFightInfo,
  getCharacterDamage,
  (state: RootState) => state.fight.characterTurnPriority,
  (
    userInfo,
    derivedStats,
    currentBaseStats,
    totalBaseStats,
    skills,
    skillsFightInfo,
    damage,
    characterTurnPriority
  ): Fighter =>
    buildFighterData(
      userInfo.userName || "You",
      derivedStats.current.complete,
      currentBaseStats,
      totalBaseStats,
      skills,
      skillsFightInfo.skillsRecharge,
      skillsFightInfo.skillsValues,
      damage,
      characterTurnPriority,
      CHARACTER_FIGHTER_ACTIONS
    )
);

// OPPONENT

export const getOpponentSlug = (state: RootState): string =>
  state.fight.opponent.slug;

export const getOpponentMobData = createSelector(
  getOpponentSlug,
  (opponentSlug): MobModel => {
    const mobData = getMobData(opponentSlug);

    return mobData;
  }
);

export const getOpponentBaseInfo = createSelector(
  getOpponentMobData,
  ({
    name,
    medallions,
    images,
    position,
    isTutorial = false,
  }): {
    name: string;
    medallions: number;
    images: MobImages;
    position: number;
    isTutorial: boolean;
  } => {
    return {
      name,
      medallions,
      images,
      position,
      isTutorial,
    };
  }
);

export const getOpponentTotalBaseStats = createSelector(
  getOpponentMobData,
  (mobData): CharacterBaseStats => {
    const { medallions, baseStatsModifiers, baseStatsCosts, skills } = mobData;

    const totalBaseStats = calculateMobBaseStats(
      medallions,
      baseStatsModifiers,
      baseStatsCosts,
      skills
    );

    return totalBaseStats;
  }
);

export const getOpponentWeakenedBaseStats = (
  state: RootState
): CharacterBaseStats => state.fight.opponent.weakenedBaseStats;

export const getOpponentCurrentBaseStats = createSelector(
  getOpponentTotalBaseStats,
  getOpponentWeakenedBaseStats,
  (totalBaseStats, weakenedBaseStats): CharacterBaseStats => {
    const currentBaseStats = calculateCurrentBaseStats(
      totalBaseStats,
      weakenedBaseStats
    );

    return currentBaseStats;
  }
);

export const getOpponentDerivedStats = createSelector(
  getOpponentMobData,
  getOpponentCurrentBaseStats,
  getOpponentTotalBaseStats,
  (mobData, currentBaseStats, totalBaseStats): DerivedStatSets => {
    const { installedUpgrades } = mobData;

    const derivedStats = getDerivedStatSets(
      currentBaseStats,
      totalBaseStats,
      installedUpgrades
    );

    return derivedStats;
  }
);

export const getOpponentHealth = (state: RootState): number =>
  state.fight.opponent.health;

export const getOpponentDamage = (state: RootState): DamageModel[] =>
  state.fight.opponent.damage;

export const getOpponentPilotSkills = createSelector(
  getOpponentMobData,
  getDerivedStats,
  (mobData, derivedStats): SkillModel[] => {
    const { skills } = mobData;
    const { fasterRecharge } = derivedStats.current.complete;

    const opponentSkills = getOpponentSkills(skills, fasterRecharge);

    return opponentSkills;
  }
);

export const getOpponentSkillsFightInfo = createSelector(
  (state: RootState) => state.fight.opponent.skillsRecharge,
  (state: RootState) => state.fight.opponent.skillsValues,
  (
    skillsRecharge,
    skillsValues
  ): {
    skillsRecharge: { [key: string]: number };
    skillsValues: { [key: string]: number };
  } => {
    return {
      skillsRecharge,
      skillsValues,
    };
  }
);

export const getOpponentTargets = createSelector(
  getOpponentMobData,
  ({ baseStatsTargets }): (keyof CharacterBaseStats)[] => {
    return baseStatsTargets;
  }
);

export const getOpponentFighterData = createSelector(
  getOpponentBaseInfo,
  getOpponentDerivedStats,
  getOpponentCurrentBaseStats,
  getOpponentTotalBaseStats,
  getOpponentPilotSkills,
  getOpponentSkillsFightInfo,
  getOpponentDamage,
  (state: RootState) => state.fight.opponentTurnPriority,
  (
    baseInfo,
    derivedStats,
    currentBaseStats,
    totalBaseStats,
    skills,
    skillsFightInfo,
    damage,
    opponentTurnPriority
  ): Fighter =>
    buildFighterData(
      baseInfo.name,
      derivedStats.current.complete,
      currentBaseStats,
      totalBaseStats,
      skills,
      skillsFightInfo.skillsRecharge,
      skillsFightInfo.skillsValues,
      damage,
      opponentTurnPriority,
      OPPONENT_FIGHTER_ACTIONS
    )
);

export const getOpponentDialogs = createSelector(
  getOpponentMobData,
  ({ dialogs }): MobDialogsModel => {
    return dialogs;
  }
);

export const getOpponentDrops = createSelector(
  getOpponentMobData,
  ({ drops }): MobDrops => {
    return drops;
  }
);

export const getOpponentAnimations = (state: RootState): string[] =>
  state.fight.opponent.animations;

// FIGHT

export const getFightStatus = (state: RootState): FightStatuses =>
  state.fight.status;

export const getFightTurnsInfo = createSelector(
  (state: RootState) => state.fight.characterTurnPriority,
  (state: RootState) => state.fight.opponentTurnPriority,
  getDerivedStats,
  getOpponentDerivedStats,
  (state: RootState) => state.fight.areActionsEnabled,
  (
    characterTurnPriority,
    opponentTurnPriority,
    characterDerivedStats,
    opponentDerivedStats,
    areActionsEnabled
  ): {
    isCharacterCurrentTurn: boolean;
    isCharacterNextTurn: boolean;
    areActionsEnabled: boolean;
  } => {
    const { isCharacterCurrentTurn, isCharacterNextTurn } = getFightTurns(
      characterTurnPriority,
      opponentTurnPriority,
      characterDerivedStats.current.complete.attackSpeed,
      opponentDerivedStats.current.complete.attackSpeed
    );

    return {
      isCharacterCurrentTurn,
      isCharacterNextTurn,
      areActionsEnabled,
    };
  }
);

export const getLastFightAction = (state: RootState): FightAction | null =>
  state.fight.lastAction;

export const getCombatLogInfo = createSelector(
  (state: RootState) => state.fight.combatLogs,
  (state: RootState) => state.fight.isCombatLogOpen,
  (
    combatLogs,
    isCombatLogOpen
  ): {
    combatLogs: CombatLog[];
    isCombatLogOpen: boolean;
  } => {
    return {
      combatLogs,
      isCombatLogOpen,
    };
  }
);

export const getFightResults = (state: RootState): FightResults =>
  state.fight.results;
